// Settings · Industry pack (`/settings/industry-pack`, spec 02 §4 S13, D-343):
// the installed pack, whether the deployed pack is newer, and the previewed
// update. Merged into `onboardingEn` as `onboarding.packUpdate.*` so the parity
// gate covers it. ru is TRANSLATED (pack-update.ru.ts); zh and fr render
// English through the parity-gated placeholder (D-122).
//
// A pack ref (`ps.offer_sent`, `kg`) is configuration and is shown as code,
// never translated. The KIND of an object is named here.

export const packUpdateEn = {
  title: "Industry pack",
  subtitle: "The template your factory was set up from, and its updates.",
  loading: "Loading your industry pack…",
  errorTitle: "Could not load your industry pack",
  errorDetail: "The installed pack could not be read. Try again.",
  forbiddenTitle: "Only a group admin can manage the industry pack",
  forbiddenBody:
    "Updating the pack changes definitions every facility uses, so it is kept to group admins. Ask yours if something here needs to change.",
  emptyTitle: "No industry pack is installed yet",
  emptyBody: "Choose and install a pack in the setup wizard first.",
  emptyCta: "Open setup",
  installed: {
    heading: "Installed",
    pack: "Pack",
    version: "Version",
    fingerprint: "Content fingerprint",
    installedAt: "Installed on",
    live: "Factory live",
    liveYes: "Yes",
    liveNo: "Not yet",
    fingerprintHint:
      "The first characters of the pack's content hash. Two installs with the same fingerprint hold the same pack.",
    mode: {
      install: "Installed",
      update: "Updated",
    },
    status: {
      installing: "Applying…",
      installed: "Current",
      failed: "Failed",
      superseded: "Replaced",
    },
    applying:
      "Applying the update. This takes a minute or two; the page refreshes when it is done.",
    failed: "The last install or update did not finish. Review the update again to retry it.",
  },
  available: {
    heading: "Available",
    upToDate: "Your pack is up to date.",
    upToDateWithDeclined:
      "Your pack is up to date. Changes you did not take, still on offer: {{count}}.",
    newer: "Version {{version}} is available.",
    chip: "Update available",
    review: "Review update",
    previewError: "The update preview could not be read. Try again.",
    applying: "An update is being applied. What is available will show once it has finished.",
    counts:
      "New: {{added}} · Changed: {{changed}} · Dropped: {{removed}} · Not yet in your factory: {{missing}} · Changes on offer: {{acceptable}}",
    retry: "Retry",
  },
  drawer: {
    heading: "Update to {{version}}",
    label: "Review the pack update",
    close: "Close",
    intro: "New items are added. Changed items apply only if you tick them.",
    liveNote:
      "Your factory is live. Nothing here is deleted, and nothing you have edited is overwritten.",
    nothing: "Nothing in this version changes anything you hold.",
    selectAll: "Tick every change",
    selectNone: "Untick all",
    summary: {
      applies: "Items to add: {{count}}",
      accepted: "Changes ticked: {{count}}",
    },
    apply: "Apply update",
    applying: "Applying…",
    cancel: "Cancel",
    fields: "Changed: {{fields}}",
    accept: "Take this change",
  },
  change: {
    added: "New",
    changed: "Changed",
    removed: "Dropped by the pack",
    missing: "Not yet in your factory",
  },
  disposition: {
    applies: "Will be added",
    acceptable: "Yours to choose",
    customized: "Edited by your team, kept",
    adopt_only: "Your settings are kept",
    kept: "Kept, never deleted",
    pending_module: "Kept until its module is in use",
  },
  kinds: {
    unit: "Units",
    attribute_schema: "Attribute sets",
    catalogue_template: "Catalogue templates",
    qc_checklist_template: "Quality checks",
    failure_cause: "Failure causes",
    waste_category: "Waste categories",
    remnant_price_class: "Remnant price classes",
    waste_settings: "Waste settings",
    waste_cause: "Waste causes",
    downtime_reason: "Downtime reasons",
    maintenance_reason_code: "Maintenance reasons",
    offcut_discount_class: "Off-cut discount classes",
    pipeline_stage: "Deal stages",
    role: "Roles",
    terminology_bundle: "Terminology",
    routing_step_template: "Routing step templates",
    work_center_template: "Work centre templates",
    machine_type: "Machine types",
    label_template: "Label templates",
  },
  problems: {
    PACK_UPDATE_STALE:
      "A newer pack was deployed while you were reviewing. The preview has been refreshed; review it again.",
    PACK_UP_TO_DATE: "There is nothing left to apply.",
    PACK_UPDATE_NOT_ACCEPTABLE:
      "One of the ticked changes can no longer be taken. The preview has been refreshed.",
    PACK_INSTALL_IN_PROGRESS: "An install or update is already running. Wait for it to finish.",
    generic: "The update could not be started. Try again.",
  },
  applied: "The update was applied.",
} as const;
